<template>
  <transition name="dp-fade">
    <div v-if="visible" class="dp-mask" @click.self="cancel">
      <div class="dp-panel">
        <div class="dp-head">
          <span class="dp-cancel" @click="cancel">取消</span>
          <span class="dp-title">{{ title }}</span>
          <span class="dp-ok" @click="confirm">确定</span>
        </div>
        <div class="dp-body" ref="body">
          <div class="dp-line dp-line-top"></div>
          <div class="dp-line dp-line-bottom"></div>
          <div class="dp-active-row" aria-hidden="true">
            <span class="dp-active-cell">{{ displayYear }}</span>
            <span class="dp-active-cell">{{ displayMonth }}</span>
            <span class="dp-active-cell">{{ displayDay }}</span>
          </div>
          <div
            v-for="w in wheels"
            :key="w.key"
            class="dp-wheel"
            :ref="w.key + 'Wheel'"
            @touchstart.prevent="onStart($event, w.key)"
            @touchmove.prevent="onMove($event, w.key)"
            @touchend="onEnd($event, w.key)"
            @mousedown.prevent="onStart($event, w.key)"
            @mousemove.prevent="onMove($event, w.key)"
            @mouseup="onEnd($event, w.key)"
            @mouseleave="onEnd($event, w.key)"
          >
            <div class="dp-wheel-list" :style="listStyle(w.key)">
              <div
                v-for="(it, i) in w.items"
                :key="w.key + '_' + it.value"
                class="dp-item"
                :class="{ 'dp-item-active': i === w.index }"
              >{{ it.text }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'MDatePicker',
  props: {
    visible: {type: Boolean, default: false},
    value: {type: [Number, String, Date], default: ''},
    title: {type: String, default: '选择日期'},
    minYear: {type: Number, default: 2020},
    maxYear: {type: Number, default: 2055},
    maxDate: {
      type: Date,
      default: function () {
        const d = new Date()
        d.setHours(23, 59, 59, 999)
        return d
      }
    },
    minDate: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      selYear: 2026,
      selMonth: 1,
      selDay: 1,
      dragging: false,
      dragDelta: {year: 0, month: 0, day: 0},
      startY: {year: 0, month: 0, day: 0},
      startOffset: {year: 0, month: 0, day: 0},
      // 运行时测量得到的真实像素值（规避 mobileRem 把 px 转 rem 后不同屏宽错位）
      itemH: 44,
      padTop: 88
    }
  },
  computed: {
    startYear() {
      return this.minDate
        ? Math.max(this.minYear, this.minDate.getFullYear())
        : this.minYear
    },
    endYear() {
      return Math.min(this.maxYear, this.maxDate.getFullYear())
    },
    yearItems() {
      const arr = []
      for (let y = this.startYear; y <= this.endYear; y++) {
        arr.push({value: y, text: y + '年'})
      }
      return arr
    },
    monthItems() {
      const b = this.monthBounds(this.selYear)
      const arr = []
      for (let m = b.minM; m <= b.maxM; m++) arr.push({value: m, text: this.pad(m) + '月'})
      return arr
    },
    dayItems() {
      const b = this.dayBounds(this.selYear, this.selMonth)
      const arr = []
      for (let d = b.minD; d <= b.maxD; d++) arr.push({value: d, text: this.pad(d) + '日'})
      return arr
    },
    wheels() {
      return [
        {key: 'year', items: this.yearItems, index: this.indexOf(this.yearItems, this.selYear)},
        {key: 'month', items: this.monthItems, index: this.indexOf(this.monthItems, this.selMonth)},
        {key: 'day', items: this.dayItems, index: this.indexOf(this.dayItems, this.selDay)}
      ]
    },
    displayYear() {
      return this.selYear + '年'
    },
    displayMonth() {
      return this.pad(this.selMonth) + '月'
    },
    displayDay() {
      return this.pad(this.selDay) + '日'
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.resetSelection()
        this.$nextTick(() => this.updateMetrics())
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.resetSelection()
      this.$nextTick(() => this.updateMetrics())
    }
  },
  methods: {
    pad(n) {
      return n < 10 ? '0' + n : '' + n
    },
    indexOf(list, val) {
      const i = list.findIndex(x => x.value === val)
      return i < 0 ? 0 : i
    },
    parseValue() {
      if (!this.value) return new Date()
      const d = new Date(Number(this.value))
      return isNaN(d.getTime()) ? new Date() : d
    },
    // 计算某年可选月份范围 [minM, maxM]，同时受 minDate / maxDate 约束
    monthBounds(y) {
      const minM = (this.minDate && y === this.startYear) ? (this.minDate.getMonth() + 1) : 1
      const maxM = (y === this.endYear) ? (this.maxDate.getMonth() + 1) : 12
      return {minM, maxM}
    },
    // 计算某年某月可选日期范围 [minD, maxD]，同时受 minDate / maxDate 约束
    dayBounds(y, m) {
      let minD = 1
      let maxD = new Date(y, m, 0).getDate()
      if (this.minDate && y === this.startYear && m === (this.minDate.getMonth() + 1)) {
        minD = this.minDate.getDate()
      }
      if (y === this.endYear && m === (this.maxDate.getMonth() + 1)) {
        maxD = this.maxDate.getDate()
      }
      return {minD, maxD}
    },
    resetSelection() {
      const d = this.parseValue()
      let y = Math.max(this.startYear, Math.min(this.endYear, d.getFullYear()))
      this.selYear = y
      const mb = this.monthBounds(y)
      this.selMonth = Math.max(mb.minM, Math.min(mb.maxM, d.getMonth() + 1))
      const db = this.dayBounds(y, this.selMonth)
      this.selDay = Math.max(db.minD, Math.min(db.maxD, d.getDate()))
      this.dragDelta = {year: 0, month: 0, day: 0}
    },
    // 运行时测量真实行高/内边距，保证任意屏宽下三列严格居中对齐
    updateMetrics() {
      const body = this.$refs.body
      if (!body) return
      const wheelEl = body.querySelector('.dp-wheel')
      const itemEl = body.querySelector('.dp-item')
      if (!wheelEl || !itemEl) return
      const wheelH = wheelEl.clientHeight
      const itemH = itemEl.offsetHeight
      if (wheelH > 0 && itemH > 0) {
        this.itemH = itemH
        this.padTop = (wheelH - itemH) / 2
      }
    },
    itemsFor(key) {
      return key === 'year' ? this.yearItems : (key === 'month' ? this.monthItems : this.dayItems)
    },
    indexFor(key) {
      const w = this.wheels.find(x => x.key === key)
      return w ? w.index : 0
    },
    listStyle(key) {
      const idx = this.indexFor(key)
      const base = -idx * this.itemH
      const delta = this.dragging ? this.dragDelta[key] : 0
      return {
        transform: 'translateY(' + (base + delta) + 'px)',
        'padding-top': this.padTop + 'px',
        'padding-bottom': this.padTop + 'px',
        transition: this.dragging ? 'none' : 'transform .2s ease-out'
      }
    },
    onStart(e, key) {
      const t = (e.touches && e.touches[0]) || e
      this.startY[key] = t.clientY
      this.startOffset[key] = -this.indexFor(key) * this.itemH
      this.dragDelta[key] = 0
      this.dragging = true
    },
    onMove(e, key) {
      if (!this.dragging) return
      const t = (e.touches && e.touches[0]) || e
      const dy = t.clientY - this.startY[key]
      let off = this.startOffset[key] + dy
      const maxIndex = this.itemsFor(key).length - 1
      const minOff = -maxIndex * this.itemH
      if (off > 0) off = 0
      if (off < minOff) off = minOff
      this.dragDelta[key] = off - this.startOffset[key]
    },
    onEnd(e, key) {
      if (!this.dragging) return
      const maxIndex = this.itemsFor(key).length - 1
      const moved = Math.abs(this.dragDelta[key] || 0)
      const tapThreshold = this.itemH * 0.4
      if (moved < tapThreshold) {
        // 视为「点选」：优先用触点正下方的 DOM 项算下标，几何法兜底
        const t = (e.changedTouches && e.changedTouches[0]) || (e.touches && e.touches[0]) || e
        if (t && typeof t.clientY === 'number') {
          let tapIndex = -1
          const hitEl = document.elementFromPoint(t.clientX, t.clientY)
          const itemEl = hitEl && hitEl.closest ? hitEl.closest('.dp-item') : null
          if (itemEl) {
            const listEl = itemEl.parentElement
            if (listEl) tapIndex = Array.prototype.indexOf.call(listEl.children, itemEl)
          }
          if (tapIndex < 0) {
            // 兜底：以当前选中项为中心，计算触点落在第几项
            const wheelRef = this.$refs[key + 'Wheel']
            const wheelEl = Array.isArray(wheelRef) ? wheelRef[0] : wheelRef
            if (wheelEl) {
              const rect = wheelEl.getBoundingClientRect()
              const ty = t.clientY - rect.top
              tapIndex = this.indexFor(key) + Math.round((ty - this.padTop - this.itemH / 2) / this.itemH)
            }
          }
          if (tapIndex >= 0) {
            tapIndex = Math.max(0, Math.min(maxIndex, tapIndex))
            this.applyIndex(key, tapIndex)
          }
        }
      } else {
        // 视为「拖动」：归位到最近的一项
        let idx = Math.round(-(this.startOffset[key] + this.dragDelta[key]) / this.itemH)
        idx = Math.max(0, Math.min(maxIndex, idx))
        this.applyIndex(key, idx)
      }
      this.dragDelta[key] = 0
      this.dragging = false
    },
    applyIndex(key, idx) {
      const items = this.itemsFor(key)
      const item = items[idx]
      if (!item) return
      if (key === 'year') {
        this.selYear = item.value
        // 年变化后，月/日可选范围可能变化，自动 clamp
        const mb = this.monthBounds(this.selYear)
        if (this.selMonth < mb.minM) this.selMonth = mb.minM
        if (this.selMonth > mb.maxM) this.selMonth = mb.maxM
        const db = this.dayBounds(this.selYear, this.selMonth)
        if (this.selDay < db.minD) this.selDay = db.minD
        if (this.selDay > db.maxD) this.selDay = db.maxD
      } else if (key === 'month') {
        this.selMonth = item.value
        const db = this.dayBounds(this.selYear, this.selMonth)
        if (this.selDay < db.minD) this.selDay = db.minD
        if (this.selDay > db.maxD) this.selDay = db.maxD
      } else {
        this.selDay = item.value
      }
      // 选择过程中仅更新组件内部选中态，不通知外部，避免筛选面板时间实时变化
    },
    cancel() {
      this.dragging = false
      this.$emit('update:visible', false)
      this.$emit('cancel')
    },
    confirm() {
      this.dragging = false
      const ts = new Date(this.selYear, this.selMonth - 1, this.selDay, 0, 0, 0, 0).getTime()
      this.$emit('input', ts)
      this.$emit('change', ts)
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.dp-mask {
  position: fixed;
  inset: 0;
  background: rgba(12, 16, 22, .45);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  --primary: #12b3a8
}

.dp-panel {
  width: 100%;
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding-bottom: env(safe-area-inset-bottom);
  max-height: 60%;
  display: flex;
  flex-direction: column
}

.dp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: .5px solid #eef0f4;
  font-size: 15px
}

.dp-cancel {
  color: #9aa4b2;
  padding: 6px 4px
}

.dp-title {
  font-weight: 600;
  color: #14181f
}

.dp-ok {
  color: var(--primary);
  font-weight: 600;
  padding: 6px 4px
}

.dp-body {
  position: relative;
  height: 220px;
  padding: 0 16px;
  overflow: hidden;
  display: flex;
  background: #fff
}

.dp-wheel {
  flex: 1;
  height: 220px;
  position: relative;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: none;
  user-select: none
}

.dp-wheel-list {
  will-change: transform;
  padding-top: 88px;
  padding-bottom: 88px
}

.dp-item {
  height: 44px;
  line-height: 44px;
  text-align: center;
  font-size: 16px;
  color: #c7c7c7;
  cursor: pointer;
  transition: color .15s
}

.dp-item-active {
  color: #c7c7c7
}

/* 中间选中行：固定在容器中部，不随滚动移动 */
.dp-active-row {
  position: absolute;
  top: 88px;
  left: 16px;
  right: 16px;
  height: 44px;
  display: flex;
  align-items: center;
  background: #fff;
  pointer-events: none;
  z-index: 3
}

.dp-active-cell {
  flex: 1;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  color: var(--primary)
}

.dp-line {
  position: absolute;
  left: 16px;
  right: 16px;
  height: 0;
  border-top: 1px solid #e5e5e5;
  z-index: 4;
  pointer-events: none
}

.dp-line-top {
  top: 88px
}

.dp-line-bottom {
  top: 132px
}

.dp-fade-enter-active, .dp-fade-leave-active {
  transition: opacity .25s
}

.dp-fade-enter-active .dp-panel, .dp-fade-leave-active .dp-panel {
  transition: transform .3s cubic-bezier(.32, .72, 0, 1)
}

.dp-fade-enter, .dp-fade-leave-to {
  opacity: 0
}

.dp-fade-enter .dp-panel, .dp-fade-leave-to .dp-panel {
  transform: translateY(102%)
}
</style>



// WEBPACK FOOTER //
// src/components/mobile/MDatePicker.vue